import { CircleAlertIcon } from "lucide-react";
import {
  ComboBox,
  FieldError,
  Input,
  Label,
  Button as RACButton,
  TooltipTrigger,
} from "react-aria-components";
import {
  type ControllerRenderProps,
  type FieldError as RHFFieldError,
  useFormContext,
} from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import {
  ComboBoxListBox,
  ComboBoxListItem,
  ComboBoxPopover,
} from "@/components/combobox";
import { ChevronDownIcon } from "@/components/icons";
import { Tooltip } from "@/components/tooltip";
import { useGetRequestMyFiles } from "@/features/my-files/hooks/useGetRequestMyFiles";
import {
  getExtensionFromFilename,
  parseFileName,
} from "@/utils/getExtensionFromMime.ts";

import type { UploadFilesFormData } from "../schema";

interface FileComboboxProps {
  field: ControllerRenderProps<UploadFilesFormData, `files.${number}.name`>;
  fieldError: RHFFieldError | undefined;
  index: number;
  label: string;
}

export function FileCombobox({
  field,
  fieldError,
  index,
  label,
}: FileComboboxProps) {
  const { setValue, trigger, watch } = useFormContext();
  const { name, onBlur, onChange, ref, value } = field;
  const isInvalid = !!fieldError;
  const previousVersionId = watch(`files.${index}.previousVersionId`);

  const { data: myFiles } = useGetRequestMyFiles();

  const currentFileExtension = getExtensionFromFilename(label);

  const filteredFiles =
    myFiles?.filter(
      (file) => getExtensionFromFilename(file.name) === currentFileExtension,
    ) || [];

  const handleInputChange = (value: string) => {
    onChange(value);

    const matchedFile = filteredFiles?.find((file) => {
      try {
        const { baseName } = parseFileName(file.name);

        return baseName === value;
      } catch {
        return false;
      }
    });

    if (matchedFile) {
      setValue(`files.${index}.previousVersionId`, matchedFile.id);
      setValue(`files.${index}.isSourceFile`, matchedFile.isSourceFile);
    } else {
      setValue(`files.${index}.previousVersionId`, null);
    }

    // Trigger validation after input change to update error messages
    setTimeout(() => {
      trigger("files");
    }, 0);
  };

  const handleSelectionChange = (selectedFileId: null | number | string) => {
    if (selectedFileId === null) return;

    const fileId = selectedFileId as number;
    const selectedFile = filteredFiles?.find((file) => file.id === fileId);

    if (!selectedFile) return;

    // Set only the filename without extension
    try {
      const { baseName } = parseFileName(selectedFile.name);

      setValue(`files.${index}.previousVersionId`, fileId);
      setValue(`files.${index}.name`, baseName);
      setValue(`files.${index}.isSourceFile`, selectedFile.isSourceFile);
    } catch {
      // Fallback to original name if parsing fails
      setValue(`files.${index}.previousVersionId`, fileId);
      setValue(`files.${index}.name`, selectedFile.name);
      setValue(`files.${index}.isSourceFile`, selectedFile.isSourceFile);
    }

    // Trigger validation after selection to clear error messages
    setTimeout(() => {
      trigger("files");
    }, 0);
  };

  return (
    <ComboBox
      allowsCustomValue
      className="flex flex-col gap-[4px]"
      defaultItems={filteredFiles}
      inputValue={value}
      isInvalid={isInvalid}
      name={name}
      onInputChange={handleInputChange}
      onSelectionChange={handleSelectionChange}
      selectedKey={previousVersionId}
    >
      <div className="flex justify-between w-[365px] items-center">
        <Label className="text-xs block font-regular text-text-tertiary truncate">
          {label}
        </Label>
        {previousVersionId && (
          <TooltipTrigger delay={0}>
            <RACButton>
              <CircleAlertIcon className="text-fg-brand-primary" size={12} />
            </RACButton>
            <Tooltip>You&apos;re updating the previous version file</Tooltip>
          </TooltipTrigger>
        )}
      </div>
      <div className="relative w-min">
        <Input
          className={({ isFocused }) =>
            twMerge(
              "rounded-md pl-lg pr-[30px] outline-none py-sm bg-bg-primary border border-border-primary text-sm font-regular text-text-primary h-4xl w-[365px]",
              "placeholder:text-text-placeholder",
              isFocused && "border-border-brand ring-4 ring-border-brand/25",
              isInvalid && "border-border-error ring-4 ring-border-error/25",
            )
          }
          onBlur={onBlur}
          placeholder="Set file name or choose from list"
          ref={ref}
        />
        <Button
          className="absolute right-0 top-1/2 -translate-y-1/2"
          data-ui="trigger"
          isIconOnly
          size="sm"
          variant="plain"
        >
          <ChevronDownIcon className="text-text-quaternary" strokeWidth={2} />
        </Button>
      </div>
      <FieldError className="text-xs text-text-error-primary">
        {fieldError?.message}
      </FieldError>
      {filteredFiles && (
        <ComboBoxPopover>
          <ComboBoxListBox items={filteredFiles}>
            {(item) => {
              let displayName = item.name;

              try {
                const { fullNormalizedName } = parseFileName(item.name);

                displayName = fullNormalizedName;
              } catch {
                displayName = item.name;
              }

              return (
                <ComboBoxListItem id={item.id}>
                  <div className="flex gap-2 justify-between items-center w-[310px]">
                    <div className="flex gap-1 min-w-0 flex-1">
                      <span className="truncate">{displayName}</span>
                      <span className="whitespace-nowrap">
                        (ver: {item.version})
                      </span>
                    </div>
                    {item.isSourceFile && (
                      <Badge
                        isFilled={true}
                        isRounded={true}
                        size="small"
                        variant="brand"
                      >
                        Source file
                      </Badge>
                    )}
                  </div>
                </ComboBoxListItem>
              );
            }}
          </ComboBoxListBox>
        </ComboBoxPopover>
      )}
    </ComboBox>
  );
}
